import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { PenLine, BookMarked, AudioLines, ImagePlus, FileDown, Feather, ArrowRight } from "lucide-react";

const features = [
  { key: "write", icon: PenLine },
  { key: "bible", icon: BookMarked },
  { key: "voice", icon: AudioLines },
  { key: "cover", icon: ImagePlus },
  { key: "export", icon: FileDown },
];

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/dashboard" : "/register");

  return (
    <div className="min-h-screen bg-ink-900 grain relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gold/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-blue-900/20 blur-[130px] pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2.5">
          <Feather className="w-6 h-6 text-gold" />
          <div className="leading-none">
            <span className="font-serif text-2xl font-bold tracking-wide text-slate-100">{t("app.name")}</span>
            <span className="hidden sm:inline font-sans text-[11px] uppercase tracking-[0.25em] text-gold/70 ml-2">{t("app.tagline")}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Button variant="ghost" className="text-slate-300 hover:text-gold hover:bg-transparent" onClick={() => navigate("/login")} data-testid="landing-login-btn">
            {t("landing.cta.login")}
          </Button>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-12">
        <section className="max-w-5xl mx-auto pt-16 md:pt-24 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 text-xs font-medium text-gold-200 tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-gold" />
              {t("landing.hero.badge")}
            </span>
            <h1 className="mt-7 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-slate-50 max-w-3xl">
              {t("landing.hero.title")}
            </h1>
            <p className="mt-6 font-sans text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed">
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button onClick={go} className="bg-gold hover:bg-gold-light text-ink-900 font-semibold rounded-full px-7 h-12 text-sm gold-glow transition-all hover:scale-[1.02]" data-testid="landing-start-btn">
                {t("landing.cta.start")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </motion.div>
        </section>

        <section className="max-w-6xl mx-auto pb-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                  className="card-surface rounded-2xl p-6 hover:border-gold/40 transition-colors group"
                >
                  <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-slate-100">{t(`landing.feat.${f.key}.t`)}</h3>
                  <p className="mt-2 font-sans text-sm text-slate-400 leading-relaxed">{t(`landing.feat.${f.key}.d`)}</p>
                </motion.div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
